import { getIdToken } from "@/lib/firebase"
import { logger } from "@/lib/logger"

/**
 * Cliente HTTP unico contra la API FastAPI.
 * Adjunta el ID token de Firebase en cada request y el tenant activo,
 * que es lo que el backend usa para inyectar el tenant y activar RLS.
 */

// En dev se usa el proxy de Vite (/api -> localhost:8000).
// En produccion el Dockerfile hornea VITE_API_URL con la URL publica del backend.
const BASE_URL = (
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "/api"
).replace(/\/$/, "")

/** Mientras el backend no exponga los endpoints, las rutas sirven datos mock. */
export const USE_MOCKS = import.meta.env.VITE_USE_MOCKS !== "false"

/**
 * Sesion de desarrollo sin Firebase.
 *
 * `USE_MOCKS` mezclaba dos cosas que no son la misma: simular los DATOS y
 * simular la AUTENTICACION. Mientras estuvieran juntas, trabajar contra el
 * backend real sin credenciales de Firebase era imposible — la pantalla de
 * login tiraba "Firebase no esta configurado" y no habia forma de pasar.
 *
 * Con esto se separan: `VITE_USE_MOCKS=false` + `VITE_AUTH_DEV_BYPASS=true`
 * pega contra la API de verdad y entra sin Firebase.
 *
 * Solo aplica si Firebase NO esta configurado, para que no pueda tapar un
 * login real. Y no es un agujero de seguridad: el backend sigue exigiendo un
 * token valido, asi que con esto puesto contra un backend de produccion la SPA
 * entra y despues recibe 401 en todo.
 */
export const AUTH_DEV_BYPASS = import.meta.env.VITE_AUTH_DEV_BYPASS === "true"

export class ApiError extends Error {
  status: number
  detail: unknown
  /**
   * El `X-Request-Id` que devolvio el backend.
   *
   * Es la pieza que une las dos mitades de un incidente: con el mismo id, lo
   * que vio el navegador y lo que paso en el servidor son la misma linea de
   * investigacion. Por eso viaja hasta el toast que ve el usuario.
   *
   * En produccion, con la SPA en otro origen, el navegador solo puede leer este
   * header si el backend lo publica en `expose_headers` del CORS. En desarrollo
   * y en las pruebas de punta a punta se pasa por el proxy de Vite (mismo
   * origen) y siempre esta.
   */
  requestId: string | null

  constructor(status: number, message: string, detail?: unknown, requestId?: string | null) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
    this.requestId = requestId ?? null
  }
}

let activeTenantId: string | null = null

/** Lo setea el AuthProvider cuando cambia el tenant seleccionado. */
export function setActiveTenantHeader(tenantId: string | null) {
  activeTenantId = tenantId
}

export function getActiveTenantHeader() {
  return activeTenantId
}

/**
 * Entidad (sociedad) activa.
 *
 * Vive al lado del tenant y por la misma razon: son las dos coordenadas del
 * alcance de todo lo que se pide. La diferencia esta en COMO viajan — el tenant
 * es un header (`X-Tenant-Id`, es lo que activa RLS en Postgres) y la entidad es
 * un `entity_id` en la query o en el cuerpo, porque el backend la declara asi en
 * cada endpoint. Por eso no hay `setActiveEntityHeader`: no es un header.
 *
 * Antes esto salia de `VITE_ENTITY_ID`, una variable de entorno que ademas
 * estaba vacia: la SPA no tenia el concepto de entidad y toda alta de contrato,
 * centro de costo o persona moria con un 422 nombrando el campo. Ahora sale de
 * la sesion (ver `lib/activeEntity.ts` y `AuthProvider`).
 */
let activeEntityId: string | null = null

/** Lo setea el AuthProvider cuando cambia la entidad seleccionada. */
export function setActiveEntityId(entityId: string | null) {
  activeEntityId = entityId
}

export function getActiveEntityId() {
  return activeEntityId
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"

interface RequestOptions {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  tenantId?: string
  headers?: Record<string, string>
}

function buildUrl(path: string, query?: RequestOptions["query"]) {
  const url = `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  const token = await getIdToken()

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  }
  if (options.body !== undefined) headers["Content-Type"] = "application/json"
  if (token) headers.Authorization = `Bearer ${token}`

  const tenantId = options.tenantId ?? activeTenantId
  if (tenantId) headers["X-Tenant-Id"] = tenantId

  const started = Date.now()
  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      signal: options.signal,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch (error) {
    // Un fallo de red no es lo mismo que un 500: no hubo servidor al otro lado,
    // asi que no hay `request_id` con el que cruzarlo y el log del backend no
    // tiene nada de esto. Se distingue en el campo `tipo`.
    //
    // Una peticion cancelada (cambio de pantalla, filtro nuevo) no es un fallo:
    // loguearla llenaria el log de ruido que nadie tiene que mirar.
    const abortada = error instanceof Error && error.name === "AbortError"
    if (!abortada) {
      logger.error("fallo de red contra la API", {
        tipo: "red",
        metodo: method,
        ruta: path,
        ms: Date.now() - started,
        motivo: error instanceof Error ? error.message : String(error),
      })
    }
    throw error
  }

  const requestId = response.headers.get("X-Request-Id")

  if (!response.ok) {
    // Se loguea TODA respuesta no-ok, tambien las que la pantalla ya muestra:
    // el toast se va a los seis segundos y el log queda. Un 4xx es casi siempre
    // esperable (sesion vencida, permiso, validacion) y va como aviso; un 5xx es
    // un incidente del servidor y va como error.
    const nivel = response.status >= 500 ? "error" : "warn"
    logger[nivel]("la API respondio con error", {
      tipo: "http",
      metodo: method,
      ruta: path,
      estado: response.status,
      ms: Date.now() - started,
      request_id: requestId,
    })
  }

  if (response.status === 204) return undefined as T

  const isJson = response.headers.get("content-type")?.includes("application/json")
  const payload = isJson ? await response.json().catch(() => null) : await response.text()

  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in (payload as Record<string, unknown>)
        ? (payload as Record<string, unknown>).detail
        : payload
    throw new ApiError(response.status, messageForStatus(response.status, detail), detail, requestId)
  }

  return payload as T
}

function messageForStatus(status: number, detail: unknown) {
  if (typeof detail === "string" && detail.trim()) return detail
  switch (status) {
    case 401:
      return "Sesion expirada. Vuelve a iniciar sesion."
    case 403:
      return "No tienes permiso para esta accion en este alcance."
    case 404:
      return "No encontrado."
    case 409:
      return "Conflicto: el periodo puede estar cerrado."
    case 422:
      return "Datos invalidos."
    default:
      return `Error ${status} en la API.`
  }
}

export const api = {
  get: <T,>(path: string, options?: RequestOptions) => request<T>("GET", path, options),
  post: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, { ...options, body }),
  patch: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, { ...options, body }),
  put: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, { ...options, body }),
  delete: <T,>(path: string, options?: RequestOptions) => request<T>("DELETE", path, options),
}

/**
 * Descarga un archivo de la API y lo entrega al navegador.
 *
 * NO se puede hacer con un `<a href>`: la peticion necesita el token de
 * Firebase y la cabecera `X-Tenant-Id`, y un enlace no los lleva. Sin eso el
 * backend responde 401 y el navegador se descarga la pagina de error con
 * extension .csv, que es peor que fallar — el archivo existe y esta vacio.
 *
 * El nombre sale del `Content-Disposition` que manda el backend, porque es el
 * backend quien sabe de que periodo y de que alcance es lo que acaba de
 * generar. `respaldo` solo se usa si la cabecera no llega.
 */
export async function descargarArchivo(
  path: string,
  respaldo: string,
  /**
   * El tenant al que pertenece el archivo, cuando NO es el activo.
   *
   * La consola del contador es cross-tenant: un despacho ve las declaraciones
   * de sus veinte clientes sin cambiar de cliente activo. Sin este parámetro,
   * la descarga salía con el `X-Tenant-Id` del cliente que estuviera elegido en
   * la barra, y el backend respondía 404 —«no existe ese período en este
   * tenant»— sobre una fila que se estaba viendo en pantalla.
   */
  tenantId?: string,
): Promise<void> {
  const token = await getIdToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const tenant = tenantId ?? activeTenantId
  if (tenant) headers["X-Tenant-Id"] = tenant

  const response = await fetch(buildUrl(path), { method: "GET", headers })
  const requestId = response.headers.get("X-Request-Id")
  if (!response.ok) {
    logger.warn("la API respondio con error al descargar", {
      tipo: "http", metodo: "GET", ruta: path,
      estado: response.status, request_id: requestId,
    })
    const detalle = await response.text().catch(() => "")
    throw new ApiError(
      response.status, messageForStatus(response.status, detalle), detalle, requestId,
    )
  }

  const disposicion = response.headers.get("Content-Disposition") ?? ""
  const nombre = /filename="?([^";]+)"?/.exec(disposicion)?.[1] ?? respaldo

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = nombre
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Sin revocar, cada descarga deja el archivo entero retenido en memoria
  // mientras viva la pestaña. Con un CSV de un directorio no se nota; con
  // veinte, si.
  URL.revokeObjectURL(url)
}

/**
 * Sube un archivo (multipart) a la API.
 *
 * NO se puede usar `api.post`: aquel serializa el cuerpo a JSON y fija
 * `Content-Type: application/json`. Con un `FormData` hay que hacer lo
 * CONTRARIO —no tocar `Content-Type`— porque el navegador tiene que escribirlo
 * el mismo con el `boundary` que acaba de generar. Fijarlo a mano produce una
 * peticion cuyo cuerpo el servidor no puede partir, y FastAPI responde 422
 * diciendo que falta el campo `archivo`, que es un mensaje que manda a buscar
 * el error en el sitio equivocado.
 *
 * El resto —token, `X-Tenant-Id`, log de fallos, `ApiError` con `requestId`—
 * es identico a `request()` a proposito: un fallo al subir tiene que ser
 * investigable con el mismo `request_id` que cualquier otro.
 */
export async function subirArchivo<T>(
  path: string,
  form: FormData,
  options: { query?: RequestOptions["query"]; signal?: AbortSignal } = {},
): Promise<T> {
  const token = await getIdToken()
  const headers: Record<string, string> = { Accept: "application/json" }
  if (token) headers.Authorization = `Bearer ${token}`
  if (activeTenantId) headers["X-Tenant-Id"] = activeTenantId

  const started = Date.now()
  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: "POST",
      headers,
      body: form,
      signal: options.signal,
    })
  } catch (error) {
    const abortada = error instanceof Error && error.name === "AbortError"
    if (!abortada) {
      logger.error("fallo de red al subir un archivo", {
        tipo: "red",
        metodo: "POST",
        ruta: path,
        ms: Date.now() - started,
        motivo: error instanceof Error ? error.message : String(error),
      })
    }
    throw error
  }

  const requestId = response.headers.get("X-Request-Id")
  if (!response.ok) {
    const nivel = response.status >= 500 ? "error" : "warn"
    logger[nivel]("la API respondio con error al subir", {
      tipo: "http",
      metodo: "POST",
      ruta: path,
      estado: response.status,
      ms: Date.now() - started,
      request_id: requestId,
    })
  }

  const isJson = response.headers.get("content-type")?.includes("application/json")
  const payload = isJson ? await response.json().catch(() => null) : await response.text()

  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in (payload as Record<string, unknown>)
        ? (payload as Record<string, unknown>).detail
        : payload
    throw new ApiError(response.status, messageForStatus(response.status, detail), detail, requestId)
  }

  return payload as T
}

/** Latencia simulada para que los estados de carga se vean como en produccion. */
export function mockDelay<T>(value: T, ms = 180): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms))
}
